import { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { replaceWithRegion } from 'actions/NavigationActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { reportClientError } from 'services/errorReportingService'
import { isChatHandoffUnavailableResponse, type RedeemChatHandoffResult } from 'tripPlanner/api/v3'
import type { AssistantUserData } from 'tripPlanner/assistant/api/types'
import { CHAT_TRIP_PLANNER_NEW_PATH } from 'tripPlanner/config'
import { useRedeemChatHandoffMutation } from 'tripPlanner/hooks/api/chat'
import { trackEntryPoint } from 'tripPlanner/utils/chatAnalytics'
import type { ChatNavigationOptions } from './useChatNavigation'
import { getChatHandoffErrorCopy } from '../utils/chatErrorMessages'

const CHAT_HANDOFF_QUERY_PARAM = 'handoff'

interface UseChatHandoffRedemptionArgs {
  search: string
  userData: AssistantUserData
  setChatId: (chatId: number, options: ChatNavigationOptions) => void
}

interface UseChatHandoffRedemptionResult {
  isRedeemingChatHandoff: boolean
  /** The chat this page's handoff created, so the page can treat it as new rather than reopened. */
  handoffChatId: number | undefined
  /** The chat this page's handoff opened, whether it created it or an earlier redeem did. */
  redeemedHandoffChatId: number | undefined
}

/**
 * Redeems a WhatsApp "continue on the website" `?handoff=` token, then replaces the URL with the
 * new chat. `isRedeemingChatHandoff` is true until the redeem of the URL's token settles; until then the caller
 * must not mount anything that calls svc-trip without a visitor cookie, because svc-trip mints a
 * first-time visitor's cookie on their first call and the redeem has to be that call to own the
 * new chat.
 */
export function useChatHandoffRedemption({
  search,
  userData,
  setChatId,
}: UseChatHandoffRedemptionArgs): UseChatHandoffRedemptionResult {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { mutate: redeemChatHandoff } = useRedeemChatHandoffMutation()
  const redeemedTokenRef = useRef<string | undefined>(undefined)
  const [redeemedHandoff, setRedeemedHandoff] = useState<RedeemChatHandoffResult | undefined>(undefined)
  const [settledToken, setSettledToken] = useState<string | undefined>(undefined)

  const handoffToken = useMemo(() => {
    return new URLSearchParams(search).get(CHAT_HANDOFF_QUERY_PARAM)?.trim() || undefined
  }, [search])

  useEffect(() => {
    if (!handoffToken || redeemedTokenRef.current === handoffToken) {
      return
    }

    redeemedTokenRef.current = handoffToken
    redeemChatHandoff({ token: handoffToken, userData }, {
      onSuccess: (result) => {
        setSettledToken(handoffToken)
        trackEntryPoint('whatsapp_handoff')
        setRedeemedHandoff(result)
        setChatId(result.chatId, { shouldReplaceUrl: true })
      },
      onError: (error) => {
        setSettledToken(handoffToken)
        if (!isChatHandoffUnavailableResponse(error)) {
          reportClientError(error, { message: 'Failed to redeem WhatsApp chat handoff' })
        }
        const { heading, message } = getChatHandoffErrorCopy(intl, error)
        showSnackbar(message, 'critical', { heading })
        dispatch(replaceWithRegion(CHAT_TRIP_PLANNER_NEW_PATH))
      },
    })
  }, [dispatch, handoffToken, intl, redeemChatHandoff, setChatId, userData])

  return {
    // A token this page already redeemed isn't redeemed again, so it mustn't hold the loading screen either.
    isRedeemingChatHandoff: handoffToken !== undefined && settledToken !== handoffToken,
    handoffChatId: redeemedHandoff?.isNewChat ? redeemedHandoff.chatId : undefined,
    redeemedHandoffChatId: redeemedHandoff?.chatId,
  }
}
